import Dexie, {} from 'dexie';
export class PetRhythmDb extends Dexie {
    pets;
    treatments;
    treatmentLogs;
    checkins;
    vitals;
    events;
    visits;
    qolRecords;
    thresholds;
    coughLogs;
    meals;
    gaitLogs;
    labs;
    routines;
    routineLogs;
    costs;
    handoffs;
    carePlans;
    careQuestions;
    careEventGroups;
    careSupplies;
    carePassports;
    careEpisodes;
    eliminations;
    waterLogs;
    mealPresets;
    dayMarks;
    photoDays;
    attachments;
    trash;
    settings;
    syncMeta;
    localBackups;
    migrationBackups;
    constructor(name = 'petrhythm') {
        super(name);
        const schema = {
            pets: 'id, archivedAt, sortOrder, updatedAt',
            treatments: 'id, petId, [petId+active], updatedAt',
            treatmentLogs: 'id, petId, treatmentId, [petId+recordedAt], [treatmentId+scheduledAt], updatedAt',
            checkins: 'id, petId, [petId+date], updatedAt',
            vitals: 'id, petId, [petId+type+measuredAt], updatedAt',
            events: 'id, petId, [petId+occurredAt], updatedAt',
            visits: 'id, petId, visitedAt, updatedAt',
            qolRecords: 'id, petId, [petId+date], updatedAt',
            thresholds: 'id, petId, [petId+vitalType], updatedAt',
            coughLogs: 'id, petId, [petId+date], updatedAt',
            meals: 'id, petId, servedAt, updatedAt',
            gaitLogs: 'id, petId, startedAt, updatedAt',
            labs: 'id, petId, [petId+testedAt], [petId+code+testedAt], updatedAt',
            routines: 'id, petId, active, updatedAt',
            routineLogs: 'id, petId, routineId, scheduledAt, [routineId+scheduledAt], updatedAt',
            costs: 'id, petId, occurredAt, [petId+occurredAt], updatedAt',
            handoffs: 'id, petId, localDate, createdAt, expiresAt, updatedAt',
            carePlans: 'id, petId, [petId+sourceCheckedOn], updatedAt',
            careQuestions: 'id, petId, [petId+askedOn], answeredOn, updatedAt',
            careEventGroups: 'id, petId, createdAt, updatedAt',
            careSupplies: 'id, petId, [petId+checkedOn], updatedAt',
            carePassports: 'id, &petId, reviewedAt, updatedAt',
            careEpisodes: 'id, petId, status, startsAt, [petId+startsAt], updatedAt',
            attachments: 'id, petId, createdAt',
            trash: 'id, table, deletedAt',
            settings: 'id, &key, updatedAt',
            syncMeta: 'id, &key, updatedAt',
        };
        this.version(1).stores(schema);
        this.version(2).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' }).upgrade(async (tx) => {
            const exportedAt = Date.now();
            const tables = {};
            for (const table of ['pets', 'treatments', 'treatmentLogs', 'checkins', 'vitals', 'events', 'visits', 'qolRecords', 'thresholds', 'coughLogs', 'meals', 'gaitLogs', 'labs', 'routines', 'routineLogs', 'costs']) {
                tables[table] = await tx.table(table).toArray();
            }
            await tx.table('migrationBackups').put({ id: `migration-schema-${exportedAt}`, createdAt: exportedAt, snapshot: { schemaVersion: 1, exportedAt, tables } });
        });
        this.version(3).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' });
        this.version(4).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' });
        // v5 (PET-EXP-26): 관리 계획·질문·사건 묶음 3개 테이블을 추가만 한다. 기존 테이블·인덱스는 그대로다.
        this.version(5).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' });
        // v6: 보호자가 직접 센 재고만 추가한다. 기존 건강 기록과 인덱스는 변경하지 않는다.
        this.version(6).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' });
        // v7: 보호자 소유 돌봄 여권과 맡김·미용 사건 원장을 additive table로 추가한다.
        this.version(7).stores({ ...schema, localBackups: 'id, kind, createdAt', migrationBackups: 'id, createdAt' });
        // v8 (complete-services 2026-10): 배변·물그릇·자주 주는 식사·별일 없음 표시·오늘 사진 표를 추가만 한다.
        // 위 버전 블록과 공유 schema 객체는 손대지 않는다. 기존 기록·인덱스는 그대로라 변환 단계가 없고,
        // 복구 지점은 앱 시작 때 도는 하루 1회 기기 백업(createDailyBackupIfDue)이 맡는다.
        this.version(8).stores({
            ...schema,
            localBackups: 'id, kind, createdAt',
            migrationBackups: 'id, createdAt',
            eliminations: 'id, petId, [petId+occurredAt], updatedAt',
            waterLogs: 'id, petId, [petId+date], updatedAt',
            mealPresets: 'id, petId, sortOrder, updatedAt',
            dayMarks: 'id, petId, [petId+date], updatedAt',
            photoDays: 'id, petId, [petId+date], updatedAt',
        });
    }
}
export const db = new PetRhythmDb();
